/**
 * Platform identifiers & primitives. These are nominal-ish string/number
 * aliases (kept as plain types so JSON round-trips are trivial); the naming
 * carries the intent. N-player rule: seats are `SlotIndex` (0-based), never
 * `{p1, p2}` tuples.
 */

/** A registered game's stable id — lowercase kebab: `[a-z0-9-]+` (e.g. `'pong'`). */
export type GameId = string;

/** Canonical user id — an auth uid OR a bot id (`bot_<botId>`). Bots are
 *  first-class users on the platform. */
export type Uid = string;

/** A single match's id (room/session scoped). */
export type MatchId = string;

/** 0-based seat index within a match. The N-player contract keys every
 *  per-player array/map by this. */
export type SlotIndex = number;

/** Bots are first-class users: their uid is `bot_<botId>`. */
export const BOT_UID_PREFIX = 'bot_';

/** True when `uid` denotes a bot (uid starts with {@link BOT_UID_PREFIX}). */
export function isBotUid(uid: Uid): boolean {
  return uid.startsWith(BOT_UID_PREFIX);
}

/** Validation regex for a {@link GameId}. Exposed so descriptors/registries can
 *  reject malformed ids at the boundary. */
export const GAME_ID_PATTERN = /^[a-z0-9-]+$/;

/** True when `id` is a well-formed {@link GameId}. */
export function isValidGameId(id: string): boolean {
  return GAME_ID_PATTERN.test(id);
}
